import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

// Inter is self-hosted and loads nowhere unless a module asks for it.
import '@fontsource/inter/400';
import '@fontsource/inter/700';
import {
  INDUSTRY_DISPLAY,
  IndustrySlug
} from 'containers/Setting/components/RoleFormModal/customerIntelligence';
import { useLocalization } from 'hooks';

/* The face the Intelligence header renders in. Without it this header fell through to the antd
   system stack while the Customer 360 header used Inter, so the same title rendered in two
   different typefaces across the two screens. */
const FONT_STACK =
  "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

/*
  The page header, matched to the Intelligence Suite header ("Intelligence Suite / Customer Insights"
  over "Industry: Banking/BFSI") so the two screens read as one product.

  The values are taken from components/Intelligence/PageHeader and resolved through
  containers/Intelligence/theme/tokens.ts, but written as literals here rather than imported: those
  tokens are var(--is-*) references with no fallback, declared only by IntelligenceThemeScope, so
  importing them into a page outside that scope resolves every one of them to nothing.

    wrapper   padding-bottom 16, border-bottom 1px #2A344D
    title     32/400/48, #F5F7FB          (display-*)
    industry  18/700/21.6, #1EB980        (card-title-*, positive)
    gap       4 between title and industry
*/
const Header = styled.header`
  padding: 0 0 16px;
  border-bottom: 1px solid #2a344d;
`;

const Title = styled.h1`
  margin: 0;
  font-family: ${FONT_STACK};
  font-size: 32px;
  font-weight: 400;
  line-height: 48px;
  color: #f5f7fb;
`;

/* Inherits the title's ink and weight so the heading reads exactly as it did; the underline on
   hover and focus is the whole of the added affordance. antd's global `a` rule is a single element
   selector, so this class outranks it. */
const TitleLink = styled(Link)`
  color: inherit;
  text-decoration: none;

  &:hover,
  &:focus-visible {
    color: inherit;
    text-decoration: underline;
  }
`;

const Industry = styled.p`
  margin: 4px 0 0;
  font-family: ${FONT_STACK};
  font-size: 18px;
  font-weight: 700;
  line-height: 21.6px;
  color: #1eb980;
`;

export interface IDarkPageTitleProps {
  title: string;
  /**
   * Links the leading `label` of `title` at `to`. The label is passed rather than sliced off at the
   * separator so a heading that no longer opens with it renders plain instead of linking the wrong
   * words. PLA-8844.
   */
  titleLink?: { label: string; to: string };
}

const DarkPageTitle: React.FC<IDarkPageTitleProps> = ({ title, titleLink }) => {
  const { industry } = useLocalization();

  const linked = titleLink && title.startsWith(titleLink.label) ? titleLink : null;

  /* The slug arrives from unvalidated tenant config, so one the catalogue does not know is dropped
     rather than printed raw - the same rule the Intelligence header applies. */
  const industryName =
    industry && Object.prototype.hasOwnProperty.call(INDUSTRY_DISPLAY, industry)
      ? INDUSTRY_DISPLAY[industry as IndustrySlug]
      : null;

  return (
    <Header data-testid="dark-page-title">
      <Title>
        {linked ? (
          <>
            <TitleLink to={linked.to} data-testid="dark-page-title-link">
              {linked.label}
            </TitleLink>
            {title.slice(linked.label.length)}
          </>
        ) : (
          title
        )}
      </Title>
      {industryName ? <Industry>Industry: {industryName}</Industry> : null}
    </Header>
  );
};

export default DarkPageTitle;
